/**
 * The start-date picker's month: six Monday-anchored rows, and what each day is.
 *
 * ── A hand-built calendar, and the reason is in `CLAUDE.md` ──────────────────────────────────
 * "Calendar and the progression matrix are hand-built. No third-party calendar; none of them allow
 * the cell rendering this domain needs, and the two credible ones put drag or resize behind a
 * paywall." This one needs a DOT under every day the plan trains on, which is the whole reason the
 * mock draws a picker rather than a date input: a trainer choosing a Wednesday can see, before they
 * press, that Monday's session is the one that will not happen.
 *
 * ── Six rows always, never five ──────────────────────────────────────────────────────────────
 * A month needs five or six depending on where it starts, and a grid that changes height moves the
 * whole form under the trainer's cursor every time they page a month. Six is the ceiling, so six is
 * always drawn; the trailing days belong to the next month and say so.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import { addDays, atMidnight, isoDayOfWeek, mondayOf, toIsoDate } from '~/shared/lib/calendarDate';

import type { DateWindow } from './assignRequest';

/** Rows in the grid. Fixed, so paging a month cannot change the picker's height. */
export const MONTH_ROWS = 6;

/** One day in the picker. */
export interface MonthDay {
  readonly iso: string;
  readonly dayOfMonth: number;
  /** Whether it belongs to the month being shown, rather than to the one on either side of it. */
  readonly inMonth: boolean;
  /** Whether it can be chosen — inside the assignable window. */
  readonly selectable: boolean;
  /** Whether the plan trains on this weekday. The dot under the number. */
  readonly trains: boolean;
}

/** One month, drawn. */
export interface MonthGrid {
  /** Local midnight on the first of the month — what the header names and paging moves. */
  readonly month: Date;
  /** `MONTH_ROWS` rows of seven, Monday first. */
  readonly rows: readonly (readonly MonthDay[])[];
}

/** The first of the month `date` falls in, at local midnight. */
export function firstOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

/** `months` after `month`, clamped to the first — never `setMonth`, which rolls 31 January over. */
export function shiftMonth(month: Date, months: number): Date {
  return new Date(month.getFullYear(), month.getMonth() + months, 1);
}

/**
 * The grid for one month.
 *
 * `programmedWeekdays` is the plan's own ISO weekday set. A day the plan does not train on carries
 * no dot, and so does every weekday when no plan is chosen yet — which is the client entry's first
 * state and reads correctly as "nothing scheduled here yet" rather than as a plan with no sessions.
 */
export function monthGrid(
  month: Date,
  window: DateWindow,
  programmedWeekdays: readonly number[],
): MonthGrid {
  const first = firstOfMonth(month);
  const start = mondayOf(first);
  const trains = new Set(programmedWeekdays);
  const earliest = atMidnight(window.earliest).getTime();
  const latest = atMidnight(window.latest).getTime();

  const rows: MonthDay[][] = [];
  for (let row = 0; row < MONTH_ROWS; row += 1) {
    const cells: MonthDay[] = [];
    for (let column = 0; column < 7; column += 1) {
      const date = addDays(start, row * 7 + column);
      const day = date.getTime();
      cells.push({
        iso: toIsoDate(date),
        dayOfMonth: date.getDate(),
        inMonth: date.getMonth() === first.getMonth() && date.getFullYear() === first.getFullYear(),
        selectable: day >= earliest && day <= latest,
        trains: trains.has(isoDayOfWeek(date)),
      });
    }
    rows.push(cells);
  }

  return { month: first, rows };
}
